import { type ReactNode } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { Button, Dropdown, Icon, Input, TextArea } from '@ics-portal/guardian';
import { Section } from '@components/shared/Section';
import { type StringOption } from '@models/Option';
import { type ProjectFormAttributes } from '../../../ProjectForm';
import { ProjectTypeSelector } from '../../ProjectTypeSelector';

interface ProjectFormGeneralSectionProps {
    isEditMode?: boolean;
    relatedLoading: boolean;
    milestonesLoading: boolean;
    hasDeprecatedTypes: boolean;
    showDeprecated: boolean;
    projectTypeOptions: StringOption[];
    projectMilestonesOptions: StringOption[];
    statusOptions: StringOption[];
    organizationOptions: StringOption[];
    control: Control<ProjectFormAttributes>;
    onToggleDeprecated: () => void;
    validateMilestone: (value: string | undefined) => string | true;
}

export function ProjectFormGeneralSection({
    isEditMode,
    relatedLoading,
    milestonesLoading,
    hasDeprecatedTypes,
    showDeprecated,
    projectTypeOptions,
    projectMilestonesOptions,
    statusOptions,
    organizationOptions,
    control,
    onToggleDeprecated,
    validateMilestone,
}: ProjectFormGeneralSectionProps): ReactNode {
    return (
        <Section title="General" style={{ flex: 692 }}>
            <Input label="Name" required name="name" />
            <TextArea
                label="Description"
                required
                name="description"
                minLength={10}
            />
            <div className="flex align-items-center justify-content-between">
                <span className="font-bold text-uppercase block text-xs">
                    Project type:*
                </span>
                {hasDeprecatedTypes ? (
                    <Button
                        link
                        size="small"
                        className="px-1 py-0"
                        icon={<Icon name="keyboard_arrow_down" />}
                        iconPos="right"
                        onClick={onToggleDeprecated}
                        tooltip="Toggle visiblity of deprecated types"
                    >
                        {showDeprecated ? 'Hide' : 'Show'}
                    </Button>
                ) : null}
            </div>
            <Controller
                name="typeID"
                control={control}
                rules={{ required: 'Project type is required' }}
                render={({ field, fieldState }) => (
                    <ProjectTypeSelector
                        options={projectTypeOptions}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={relatedLoading || Boolean(isEditMode)}
                        showDeprecated={showDeprecated}
                        invalid={fieldState.invalid}
                    />
                )}
            />
            <div className="flex gap-3 mt-2 w-full">
                <div className="flex-1">
                    <Dropdown
                        label="Milestone"
                        required
                        className="w-full"
                        placeholder="Not selected"
                        options={projectMilestonesOptions}
                        loading={milestonesLoading}
                        disabled={
                            milestonesLoading ||
                            !projectMilestonesOptions.length
                        }
                        name="milestoneID"
                        validate={validateMilestone}
                    />
                </div>
                <div className="flex-1">
                    <Dropdown
                        label="Status"
                        required
                        className="w-full"
                        placeholder="Not selected"
                        options={statusOptions}
                        loading={relatedLoading}
                        disabled={relatedLoading}
                        name="statusID"
                    />
                </div>
            </div>
            <Dropdown
                label="Organization"
                required
                className="w-full"
                placeholder="Not selected"
                options={organizationOptions}
                filter
                virtualScrollerOptions={{ itemSize: 38 }}
                loading={relatedLoading}
                disabled={relatedLoading}
                name="organizationID"
            />
        </Section>
    );
}
